/* eslint-disable no-underscore-dangle */
import { usePaginationSearch } from '@application/utils';
import type { EducatorReviewItemData, EducatorReviewListData } from '@presentation/components';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { REVIEWS_PAGINATED_SIZE } from '../constants';
import { useGetEducatorReviews } from './useGetEducatorReviews';

export const useEducatorReviewList = (educatorId?: number) => {
  const {
    educatorReviews,
    educatorReviewsAggregate,
    educatorReviewsRefetch,
    educatorReviewsLoading,
  } = useGetEducatorReviews(educatorId);

  const { currentPage, handlePaginationChange } = usePaginationSearch({
    scrollToTopOnChange: false,
  });

  const reviewList = useMemo((): EducatorReviewListData => {
    return {
      loading: educatorReviewsLoading,
      amount: educatorReviewsAggregate?._count?._all ?? undefined,
      average: educatorReviewsAggregate?._avg?.grade ?? undefined,
      items: (educatorReviews ?? []).map((review) => {
        let image = null;
        let name = null;
        if (review?.account?.imgs?.[0]?.small) {
          image = review?.account?.imgs?.[0]?.small;
        }
        if (review?.account?.firstName && review?.account?.lastName) {
          name = `${review?.account.firstName} ${review?.account.lastName
            .charAt(0)
            .toUpperCase()}.`;
        }
        return {
          name: name ?? undefined,
          image: image ?? undefined,
          description: review?.description ?? undefined,
          average: review?.grade ?? undefined,
          date: dayjs(review?.createdAt)?.format?.('DD/MM/YYYY'),
          verified: review?.verified,
        } as EducatorReviewItemData;
      }),
      pagination: {
        forcePage: currentPage,
        onChange: async (changedPage: number) => {
          handlePaginationChange(changedPage);
          const wrapper = document.getElementsByClassName('review-list-wrapper').item(0);
          const scrollPosition = wrapper?.getBoundingClientRect().top;
          if (scrollPosition) {
            const top = scrollPosition + window.scrollY - 150;
            window.scrollTo({ left: 0, top });
          }
          await educatorReviewsRefetch({
            skip: REVIEWS_PAGINATED_SIZE * changedPage,
          });
        },
        pageCount: (educatorReviewsAggregate?._count?._all ?? 0) / REVIEWS_PAGINATED_SIZE,
      },
    } as EducatorReviewListData;
  }, [
    educatorReviews,
    educatorReviewsAggregate?._avg?.grade,
    educatorReviewsAggregate?._count?._all,
    educatorReviewsLoading,
    educatorReviewsRefetch,
    currentPage,
    handlePaginationChange,
  ]);

  return { reviewList };
};
